import type { OfferDraftRowKind } from '../../../types';
import { contributesToOfferNetTotal } from './offerEventualPosition';
import {
  effectiveHourlyRate,
  effectiveLaborMinutes,
  isHourLaborPricedRow,
  isVirtualTitleLaborRow,
  MINUTES_PER_LABOR_HOUR,
} from './offerLaborPricing';
import { marginPercent } from './offerPricing';

/** Felder einer Kalkulationszeile, die Summen, Titelsummen und Hinweise brauchen. */
export type OfferCalculationSummaryRow = {
  id: string;
  kind: OfferDraftRowKind;
  quantity: number;
  unit?: string | null;
  unit_price_vk: number;
  unit_cost_ek: number;
  surcharge_percent: number;
  labor_minutes?: number | null;
  hourly_rate?: number | null;
  isEventual?: boolean | null;
  titleLevel?: number | null;
};

export type OfferCalculationRates = {
  /** Verkaufspreis einer Lohnstunde (Standard des Angebots). */
  hourlyRate: number;
  /** Tatsächliche interne Kosten einer Lohnstunde. */
  realLaborCostRate: number;
};

/** Zwischenrechnung eines Titels — gleiche Spalten wie die Summenzeile im Fuß. */
export type OfferCalculationTitleTotals = {
  laborMinutes: number;
  laborShare: number;
  materialEk: number;
  materialVk: number;
  costs: number;
  surchargeSum: number;
  surchargeCount: number;
};

export function emptyTitleTotals(): OfferCalculationTitleTotals {
  return {
    laborMinutes: 0,
    laborShare: 0,
    materialEk: 0,
    materialVk: 0,
    costs: 0,
    surchargeSum: 0,
    surchargeCount: 0,
  };
}

/**
 * Titelsummen der Kalkulation. Eigene Summe statt computeTitleSubtotals: hier zählt wie in der
 * Spalte „Gesamt“ auch der Lohnanteil mit, nicht nur der Material-VK des Kundendokuments.
 */
export function computeOfferCalculationTitleTotals(
  rows: readonly OfferCalculationSummaryRow[],
  rates: OfferCalculationRates,
): Map<string, OfferCalculationTitleTotals> {
  const { hourlyRate, realLaborCostRate } = rates;
  const totals = new Map<string, OfferCalculationTitleTotals>();
  type Open = { id: string; acc: OfferCalculationTitleTotals };
  const stack: Open[] = [];
  for (const row of rows) {
    if (row.kind === 'title') {
      const level = Math.max(1, Math.min(4, Math.floor(row.titleLevel ?? 1)));
      while (stack.length >= level) stack.pop();
      const current = emptyTitleTotals();
      totals.set(row.id, current);
      stack.push({ id: row.id, acc: current });
      continue;
    }
    if (isVirtualTitleLaborRow(row)) continue;
    if (stack.length === 0 || !contributesToOfferNetTotal(row)) continue;
    const hourLabor = isHourLaborPricedRow(row);
    const laborMinutes = row.quantity * effectiveLaborMinutes(row);
    const laborHours = laborMinutes / MINUTES_PER_LABOR_HOUR;
    for (const open of stack) {
      open.acc.laborMinutes += laborMinutes;
      open.acc.laborShare += laborHours * effectiveHourlyRate(row, hourlyRate);
      if (!hourLabor) {
        open.acc.materialEk += row.quantity * row.unit_cost_ek;
        open.acc.materialVk += row.quantity * row.unit_price_vk;
        open.acc.surchargeSum += row.surcharge_percent;
        open.acc.surchargeCount += 1;
      }
      open.acc.costs += (hourLabor ? 0 : row.quantity * row.unit_cost_ek) + laborHours * realLaborCostRate;
    }
  }
  return totals;
}

export type OfferCalculationSummary = {
  materialEk: number;
  materialVk: number;
  /** Verkauf netto: Material-VK + Lohnanteil. */
  vk: number;
  laborMinutes: number;
  laborHours: number;
  laborShare: number;
  realLaborCosts: number;
  totalCosts: number;
  /** Rohertrag: Verkauf netto − Gesamtkosten. */
  profit: number;
  surchargeAverage: number | null;
  /** Marge in Prozent (auf 2 Nachkommastellen), `null` ohne Verkauf. */
  margin: number | null;
};

/** Summenzeile und Kalkulationsübersicht: nur Zeilen, die in die Angebotssumme eingehen. */
export function computeOfferCalculationSummary(
  rows: readonly OfferCalculationSummaryRow[],
  rates: OfferCalculationRates,
): OfferCalculationSummary {
  const { hourlyRate, realLaborCostRate } = rates;
  let materialEk = 0;
  let materialVk = 0;
  let laborMinutes = 0;
  let laborShare = 0;
  let surchargeSum = 0;
  let surchargeCount = 0;
  for (const row of rows) {
    if (isVirtualTitleLaborRow(row)) continue;
    // Schließt auch Titel, Text und Bild aus.
    if (!contributesToOfferNetTotal(row)) continue;
    const hourLabor = isHourLaborPricedRow(row);
    if (!hourLabor) {
      materialEk += row.quantity * row.unit_cost_ek;
      materialVk += row.quantity * row.unit_price_vk;
      surchargeSum += row.surcharge_percent;
      surchargeCount += 1;
    }
    const rowLaborMinutes = row.quantity * effectiveLaborMinutes(row);
    laborMinutes += rowLaborMinutes;
    laborShare += (rowLaborMinutes / MINUTES_PER_LABOR_HOUR) * effectiveHourlyRate(row, hourlyRate);
  }
  const laborHours = laborMinutes / MINUTES_PER_LABOR_HOUR;
  const realLaborCosts = laborHours * realLaborCostRate;
  const totalCosts = materialEk + realLaborCosts;
  const vk = materialVk + laborShare;
  return {
    materialEk,
    materialVk,
    vk,
    laborMinutes,
    laborHours,
    laborShare,
    realLaborCosts,
    totalCosts,
    profit: vk - totalCosts,
    surchargeAverage: surchargeCount > 0 ? surchargeSum / surchargeCount : null,
    margin: marginPercent(totalCosts, vk),
  };
}

export type OfferCalculationRowFigures = {
  /** Stundensatz der Zeile (Override oder Standard). */
  hourlyRate: number;
  laborHours: number;
  laborShare: number;
  materialVk: number;
  materialEk: number;
  /** Spalte „Gesamt“: ohne Lohnanteil, wenn die Arbeitszeit separat ausgewiesen wird. */
  lineTotal: number;
  rowCosts: number;
  margin: number | null;
};

/** Werte einer Tabellenzeile (Lohnanteil, Gesamt, Kosten, Marge) wie in der Kalkulationsliste. */
export function computeOfferCalculationRowFigures(
  row: OfferCalculationSummaryRow,
  options: OfferCalculationRates & { listLaborSeparately?: boolean },
): OfferCalculationRowFigures {
  const { hourlyRate, realLaborCostRate, listLaborSeparately = false } = options;
  const isVirtualLabor = isVirtualTitleLaborRow(row);
  const isHourLabor = isHourLaborPricedRow(row);
  const rowHourlyRate = effectiveHourlyRate(row, hourlyRate);
  const laborHours = isVirtualLabor
    ? row.quantity
    : row.quantity * (effectiveLaborMinutes(row) / MINUTES_PER_LABOR_HOUR);
  const laborShare = isVirtualLabor ? row.quantity * row.unit_price_vk : laborHours * rowHourlyRate;
  const materialVk = isHourLabor ? 0 : row.unit_price_vk;
  const materialEk = isHourLabor ? 0 : row.unit_cost_ek;
  const lineTotal =
    isVirtualLabor || listLaborSeparately
      ? row.quantity * materialVk
      : row.quantity * materialVk + laborShare;
  const rowCosts = isVirtualLabor
    ? row.quantity * realLaborCostRate
    : row.quantity * materialEk + laborHours * realLaborCostRate;
  return {
    hourlyRate: rowHourlyRate,
    laborHours,
    laborShare,
    materialVk,
    materialEk,
    lineTotal,
    rowCosts,
    margin: marginPercent(rowCosts, lineTotal),
  };
}
